// Accumulates only the wall-clock time a page is actually visible. Time while
// the document is hidden (a backgrounded tab, a switched-away window) does not
// count, so a tab left open in the background — then closed hours later — can't
// inflate `dwell_ms`. That inflation is what otherwise dominates an `avg`, and
// even a `p50` in a low-volume bucket, with a lone multi-million-ms spike.
//
// The clock measures *engaged* time rather than time-until-teardown, which is
// the quantity dwell/bounce analysis actually wants: a visit that sat unseen in
// another tab is not engagement.
//
// `now` is injected (the page passes `() => performance.now()`) so the
// accounting is pure arithmetic over a sequence of visibility transitions and
// can be tested without a real timer or a DOM.
export interface VisibleDwellClock {
  // The page became visible (foregrounded). Starts a new visible interval; a
  // no-op if the clock already considers itself visible.
  show(): void;
  // The page became hidden (backgrounded, or torn down). Settles the open
  // visible interval into the running total; a no-op if already hidden.
  hide(): void;
  // Total visible time so far, in milliseconds, including any interval still
  // open at the moment of the call.
  elapsedMs(): number;
}

export function createVisibleDwellClock(now: () => number, startVisible: boolean): VisibleDwellClock {
  let accumulated = 0;
  // The start of the current visible interval, or null while hidden.
  let visibleSince: number | null = startVisible ? now() : null;

  return {
    show(): void {
      if (visibleSince === null) visibleSince = now();
    },
    hide(): void {
      if (visibleSince !== null) {
        accumulated += now() - visibleSince;
        visibleSince = null;
      }
    },
    elapsedMs(): number {
      return accumulated + (visibleSince === null ? 0 : now() - visibleSince);
    },
  };
}
